import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Coins, Cpu, LogOut, Package, ShieldAlert, ShieldCheck } from "lucide-react";
import { Wordmark } from "@/components/Wordmark";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", label: "AI costs", icon: Cpu, id: "admin-nav-costs", end: true },
  { to: "/admin/packs", label: "Packs & settings", icon: Package, id: "admin-nav-packs" },
  { to: "/admin/credits", label: "Viewlux credits", icon: Coins, id: "admin-nav-credits" },
];

export function AccessDenied() {
  return (
    <div data-testid="access-denied" className="grid min-h-screen place-items-center bg-white px-6">
      <div className="max-w-md rounded-2xl border border-red-500/30 bg-red-500/5 p-10 text-center">
        <ShieldAlert className="mx-auto mb-4 h-10 w-10 text-red-400" />
        <h1 className="font-display text-2xl font-bold text-slate-50">Access denied</h1>
        <p className="mt-2 text-sm text-slate-400">The Viewlux Admin area is only for the administrator.</p>
        <a href="/dashboard" data-testid="access-denied-home-link" className="mt-6 inline-flex rounded-full bg-amber-500 px-5 py-2 text-sm font-semibold text-white hover:bg-amber-600">Back to my workspace</a>
      </div>
    </div>
  );
}

export default function AdminShell() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 border-b border-gold/40 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1640px] items-center justify-between gap-4 px-4 sm:px-6">
          <div className="flex items-center gap-4">
            <Wordmark to="/admin" />
            <span data-testid="admin-badge" className="flex items-center gap-1.5 rounded-full border border-sky-500/40 bg-sky-500/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-sky-200">
              <ShieldCheck className="h-3.5 w-3.5" /> Viewlux Admin
            </span>
          </div>
          <nav className="flex items-center gap-1">
            {NAV.map(({ to, label, icon: Icon, id, end }) => (
              <NavLink key={to} to={to} end={end} data-testid={id}
                className={({ isActive }) => `flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium transition-colors sm:px-4 ${isActive ? "bg-sky-500/10 text-sky-200" : "text-slate-400 hover:bg-slate-800/70 hover:text-slate-100"}`}>
                <Icon className="h-4 w-4" /><span className="hidden sm:inline">{label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button data-testid="admin-logout-button" onClick={() => { signOut(); navigate("/login"); }}
              className="flex items-center gap-2 rounded-full border border-slate-800 px-3 py-1.5 text-xs text-slate-400 transition-colors hover:border-slate-600 hover:text-slate-100">
              <LogOut className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Log out</span>
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1640px] px-4 py-10 sm:px-6"><Outlet /></main>
    </div>
  );
}
